// components/Header.jsx
import { useEffect, useState, useRef } from 'react';
import { NavLink, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';
import '../styles/theme-light.css';
import '../styles/header.css';

function CartIcon(){ 
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="9" cy="20" r="1"/><circle cx="16" cy="20" r="1"/>
      <path d="M1 1h4l2.68 12.39A2 2 0 0 0 9.62 15h7.76a2 2 0 0 0 1.98-1.64L21 6H6"/>
    </svg>
  );
}

function BellIcon(){ 
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M15 17h5l-1.4-1.4A2 2 0 0 1 18 14.2V11a6 6 0 1 0-12 0v3.2c0 .5-.2 1-.6 1.4L4 17h5"/>
      <path d="M9 17v1a3 3 0 0 0 6 0v-1"/>
    </svg>
  );
}

function MenuIcon(){ return (
  <svg viewBox="0 0 24 24" width="20" height="20" stroke="currentColor" fill="none" strokeWidth="2">
    <path d="M3 6h18M3 12h18M3 18h18"/>
  </svg>
); }
function CloseIcon(){ return (
  <svg viewBox="0 0 24 24" width="20" height="20" stroke="currentColor" fill="none" strokeWidth="2">
    <path d="M6 6l12 12M18 6l-12 12"/>
  </svg>
); }

export default function Header() {
  const nav = useNavigate();
  const { status, user, logout } = useAuth();
  const isAuthed  = status === 'authed';
  const isAdmin =
    isAuthed &&
    (user?.isAdmin || user?.role === 'ADMIN');

  const isManager =
    isAuthed &&
    (
      user?.isManager ||
      user?.role === 'ADMIN' ||
      user?.autonectPosition === 'Management' ||
      user?.autonectPosition === 'Sales'
    );

  const canManageCatalytics =
    isAuthed &&
    (
      user?.role === 'ADMIN' ||
      user?.autonectPosition === 'Management'
    );

  const [open, setOpen] = useState(false);
  const closeMenu = () => setOpen(false);

  // ----- Admin alerts (existing) -----
  const [unseenAlerts, setUnseenAlerts] = useState(0);

  // ----- User notifications (NEW) -----
  const [unread, setUnread] = useState(0);
  const [openBell, setOpenBell] = useState(false);
  const [list, setList] = useState([]);         // recent notifications
  const bellRef = useRef(null);

  const isLocal = window.location.hostname === 'localhost';
  const baseUrl = isLocal ? 'http://localhost:5000' : window.location.origin;

  // Admin alerts polling (unchanged)
  useEffect(() => {
    if (!isAdmin) return;
    let t;
    const load = async () => {
      try {
        const r = await fetch(`${baseUrl}/api/admin/alerts/unseen-count`, { credentials:'include' });
        const d = await r.json();
        setUnseenAlerts(d.unseen || 0);
      } catch {}
      t = setTimeout(load, 15000);
    };
    load();
    return () => t && clearTimeout(t);
  }, [isAdmin, baseUrl]);

  // User notifications: poll unread count
  useEffect(() => {
    if (!isAuthed) return;
    let t;
    const poll = async () => {
      try {
        const r = await fetch(`${baseUrl}/api/notifications/unread-count`, { credentials:'include' });
        const d = await r.json();
        setUnread(d.unread || 0);
      } catch {}
      t = setTimeout(poll, 20000);
    };
    poll();
    return () => t && clearTimeout(t);
  }, [isAuthed, baseUrl]);

  // Load dropdown list when opening the bell
  async function loadRecent() {
    try {
      const r = await fetch(`${baseUrl}/api/notifications?limit=12`, { credentials:'include' });
      const d = await r.json();
      setList(Array.isArray(d.items) ? d.items : []);
    } catch { setList([]); }
  }

  async function openSuggestion(n, code) {
  try {
    await fetch(`${baseUrl}/api/notifications/${n._id}/read`, {
      method: 'POST',
      credentials: 'include'
    });
  } catch {}
  nav({ pathname: '/', search: `?primary=${encodeURIComponent(code)}&ts=${Date.now()}` });
  setOpenBell(false);
}


  // Toggle dropdown and lazy-load list
  function toggleBell() {
    const next = !openBell;
    setOpenBell(next);
    if (next) loadRecent();
  }

  // Click outside to close
  useEffect(() => {
    if (!openBell) return;
    const onDoc = (e) => {
      if (bellRef.current && !bellRef.current.contains(e.target)) setOpenBell(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [openBell]);

  async function markRead(id) {
    try {
      await fetch(`${baseUrl}/api/notifications/${id}/read`, { method:'POST', credentials:'include' });
      setList(prev =>
       prev.map(n => n._id === id ? { ...n, read:true, seen:true } : n)
      );
      setUnread(u => Math.max(0, u - 1));
    } catch {}
  }

  async function markAllRead() {
    try {
      await fetch(`${baseUrl}/api/notifications/read-all`, { method:'POST', credentials:'include' });
      setList(prev => prev.map(n => ({ ...n, read:true, seen:true })));
      setUnread(0);
    } catch {}
  }


  function openNotification(n) {
    // Optional deep-links (e.g., to a converter)
    if (n.catalyticId) nav(`/admin/catalytics?id=${n.catalyticId}`);
    setOpenBell(false);
  }

  async function openNotification(n) {
  // 1) mark read
  try {
    await fetch(`${baseUrl}/api/notifications/${n._id}/read`, {
      method: 'POST',
      credentials: 'include'
    });
  } catch {}

  // 2) decide where to go
  if (n.kind === 'CATALYTIC_AVAILABLE' && n.code) {
    // send user to Home with the code prefilled (force refresh even if already on /)
    nav({
      pathname: '/',
      search: `?primary=${encodeURIComponent(n.code)}&ts=${Date.now()}`
    });
    } else if (n.kind === 'ADMIN_MESSAGE') {
      nav('/notifications');
    } else {
    nav('/notifications');
  }


  // 3) close dropdown
  setOpenBell(false);
}

// relative-ish timestamp (fallback to locale string)
function fmtWhen(d) {
  const t = new Date(d);
  const diff = (Date.now() - t.getTime()) / 1000; // seconds
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return t.toLocaleString();
}

function NotifItem({ n, onOpen, onRead, onOpenSuggestion }) {
  const isOpenable = n.kind === 'CATALYTIC_AVAILABLE' && n.code && n.openable !== false;

  return (
    <div className={`notif-card ${n.read ? '' : 'unread'}`}>
      {!n.read && <span className="notif-dot" aria-hidden />}
      <div className="notif-main">
        <div className="notif-title-row">
          <div className="notif-title">
            {n.kind === 'CATALYTIC_AVAILABLE'
              ? <>Catalytic available: <span className="mono">{n.code}</span></>
              : <>Message from Autonect</>}
          </div>
          <div className="notif-when">{fmtWhen(n.createdAt)}</div>
        </div>

        {n.codeOriginal && (
          <div className="notif-row muted">
            You searched: <span className="chip mono">{n.codeOriginal}</span>
          </div>
        )}

        {n.message && (
          <div className="notif-note">
            {n.message}
          </div>
        )}

        {Array.isArray(n.suggestions) && n.suggestions.length > 0 && (
          <div className="notif-row muted">
            Suggestions:{' '}
            {n.suggestions.map((s, i) => (
              <button
                key={i}
                type="button"
                className="chip mono chip-link"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => onOpenSuggestion(n, s)}
                title={`Search ${s}`}
              >
                {s}
              </button>
            ))}
          </div>
        )}

        {n.priceEgp != null && (
          <div className="notif-row muted">
            EGP {new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(n.priceEgp)}
          </div>
        )}
      </div>

      <div className="notif-actions">
        {isOpenable && (
          <button type="button" className="btn btn-sm btn-primary" onClick={() => onOpen(n)}>
            Open
          </button>
        )}
      </div>
    </div>
  );
}


  return (
    <header className="header">
      <div className="container header-inner">
        {/* Left: brand */}
        <Link to="/" className="brand" onClick={closeMenu}>Au<b>tonect</b></Link>

        {/* Middle: nav */}
        <nav className={`nav ${open ? 'open' : ''}`}>
          <NavLink to="/"          className={({isActive}) => `nav-link ${isActive ? 'active' : ''}`} onClick={closeMenu}>Home</NavLink>
          <NavLink to="/subscribe" className={({isActive}) => `nav-link ${isActive ? 'active' : ''}`} onClick={closeMenu}>Subscribe</NavLink>
          <NavLink to="/dashboard" className={({isActive}) => `nav-link ${isActive ? 'active' : ''}`} onClick={closeMenu}>Dashboard</NavLink>

          {isManager && !isAdmin && (
            <NavLink to="/manager/finance" className={({isActive}) => `nav-link ${isActive ? 'active' : ''}`} onClick={closeMenu}>
              Finance
            </NavLink>
          )}

            {canManageCatalytics && !isAdmin && (
              <>
                <NavLink
                  to="/manager/catalytics"
                  className={({isActive}) => `nav-link ${isActive ? 'active' : ''}`}
                  onClick={closeMenu}
                >
                  Catalytics
                </NavLink>

                <NavLink
                  to="/manager/prices"
                  className={({isActive}) => `nav-link ${isActive ? 'active' : ''}`}
                  onClick={closeMenu}
                >
                  Prices
                </NavLink>
              </>
            )}

          {isAdmin && (
            <>
              <NavLink to="/admin" className={({isActive}) => `nav-link ${isActive ? 'active' : ''}`} onClick={closeMenu}>Admin</NavLink>
              <NavLink to="/admin/alerts" className={({isActive}) => `nav-link ${isActive ? 'active' : ''}`} onClick={closeMenu}>
                <span style={{ position:'relative', display:'inline-flex', alignItems:'center', gap:6 }}>
                  <BellIcon />
                  {unseenAlerts > 0 && (
                    <span className="badge" style={{ position:'absolute', top:-8, right:-10, fontSize:10, padding:'2px 6px' }}>
                      {unseenAlerts}
                    </span>
                  )}
                </span>
              </NavLink>
            </>
          )}
          {/* Mobile-only logout inside the menu */}
              {isAuthed && (
                <button
                  className="nav-link only-mobile"
                  onClick={() => { logout(); closeMenu(); }}
                >
                  Log out
                </button>
              )}
        </nav>

        {/* Right: user controls */}
        <div className="nav-right">
          <span className="nav-user hide-sm2">{user?.name || user?.email}</span>
          {/* Cart */}
          {isAuthed && (
            <NavLink to="/carts" className="icon-link" aria-label="Cart" title="Cart">
              <CartIcon />
            </NavLink>
          )}

          {/* 🔔 User notifications: ALWAYS visible when authed, next to cart */}
          {isAuthed && (
            <div ref={bellRef} className="notif-wrap">
              <button
                type="button"
                className="icon-link"
                aria-label="Notifications"
                title="Notifications"
                onClick={toggleBell}
              >
                <span style={{ position:'relative', display:'inline-flex', alignItems:'center' }}>
                  <BellIcon />
                  {unread > 0 && (
                    <span className="badge" style={{ position:'absolute', top:-8, right:-10, fontSize:10, padding:'2px 6px' }}>
                      {unread}
                    </span>
                  )}
                </span>
              </button>

              {openBell && (
                <div className="dropdown-card">
                  <div className="dropdown-head">
                    <strong>Notifications</strong>
                    {unread > 0 && (
                      <button className="btn btn-ghost btn-sm" onClick={markAllRead}>Mark all read</button>
                    )}
                  </div>

                      <div className="dropdown-body">
                        {list.length === 0 ? (
                          <div className="muted">No notifications</div>
                        ) : (
                          list.map(n => (
                            <NotifItem
                              key={n._id}
                              n={n}
                              onRead={markRead}
                              onOpen={(n) => openNotification(n)}
                              onOpenSuggestion={(n, code) => openSuggestion(n, code)}
                            />
                          ))
                        )}
                      </div>

                  <div className="dropdown-foot">
                    <NavLink to="/notifications" onClick={() => setOpenBell(false)} className="btn btn-ghost btn-sm">
                      View all
                    </NavLink>
                  </div>
                </div>
              )}
            </div>
          )}

              {/* Auth (right side) */}
              {isAuthed ? (
                <>
                  <button
                    className="btn btn-ghost hide-sm2"
                    onClick={() => { logout(); /* no need to close menu on desktop */ }}
                  >
                    Log out
                  </button>
                </>
              ) : (
                <>
                  <NavLink to="/login"  className={({isActive}) => `nav-link ${isActive ? 'active' : ''}`}>Login</NavLink>
                  <NavLink to="/signup" className={({isActive}) => `nav-link ${isActive ? 'active' : ''}`}>Sign up</NavLink>
                </>
              )}


          {/* Mobile hamburger */}
          <button
            className="mobile-toggle"
            aria-label="Toggle menu"
            aria-expanded={open}
            onClick={() => setOpen(o => !o)}
          >
            {open ? <CloseIcon/> : <MenuIcon/>}
          </button>
        </div>
      </div>
    </header>
  );
}
